import { Modal, Typography, Tag, Space, Alert } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  ArrowRightOutlined,
  EditOutlined,
  DeleteOutlined
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { getUnitAcronym, cleanName, getActiveEarliestUnit } from "../../../../utils/ouberUtils";

dayjs.extend(customParseFormat);

const { Text, Title } = Typography;

const AdminViewTripModal = ({ isOpen, onClose, tripData, onDeleteTrip, onEditTrip, onUpdateSuccess }) => {
  const { details: profile, account_types = [] } = useSelector((state) => state.accountDetailSlice);
  const isSuperAdmin = profile?.account_type === "Super Admin" || account_types.includes("superadmin") || account_types.includes("xadmin");

  if (!tripData) return null;

  const isOwner = tripData.created_by_employee_id && tripData.created_by_employee_id === profile?.id;
  const tripStatus = tripData.status || tripData.trip_status;
  // Edit stays open while a trip is in progress (e.g. to fix a slipping
  // arrival time), but locks once completed or cancelled.
  const isEditLockedStatus = ["Completed Trip", "Completed", "Cancelled by AO", "Cancelled by User"].includes(tripStatus);
  // Delete/cancel additionally locks while in progress — a driver already
  // out on the road shouldn't have their trip cancelled out from under them.
  const isDeleteLockedStatus = ["Completed Trip", "Completed", "Cancelled by AO", "Cancelled by User", "In Progress"].includes(tripStatus);
  const canEdit = isSuperAdmin || (isOwner && !isEditLockedStatus);
  const canDelete = isSuperAdmin || (isOwner && !isDeleteLockedStatus);

  const headerBgColor = "#862633"; 

  const formatTime = (timeStr) => {
    if (!timeStr) return null;
    if (timeStr.includes("AM") || timeStr.includes("PM")) return timeStr;
    const [h, m] = timeStr.split(":");
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    return `${hour % 12 || 12}:${m} ${ampm}`;
  };

  const parseCapacity = (capNum) => {
    if (!capNum) return { current: 0, total: 0, isFull: false };
    const current = tripData.passengers ? tripData.passengers.length : 0;
    return { current, total: capNum, isFull: current >= capNum };
  };

  const { isFull, current, total } = parseCapacity(tripData.trip_passenger_capacity || tripData.capacity || "1");

  const pickupLocation = tripData.pickup_venue || tripData.pickup;
  const dropoffLocation = tripData.dropoff_location || tripData.destination;
  const tripDate = tripData.trip_date || tripData.date;
  const purpose = tripData.trip_purpose || tripData.purpose || "Official Business";
  
  // Robust Deadline Calculation
  const dDate = tripData.deadline_date || (tripDate ? dayjs(tripDate).subtract(1, 'day').format('YYYY-MM-DD') : null);
  const dTime = tripData.deadline_time || "17:00:00";
  const deadlineDayjs = dDate ? dayjs(`${dDate} ${dTime}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD h:mm a"]) : null;

  const getStatusIcon = (status) => {
    switch(status) {
      case "Available": 
      case "Completed": return <CheckCircleOutlined style={{ color: "#33A780" }}/>; 
      case "Unavailable":
      case "Cancelled": return <CloseCircleOutlined style={{ color: "#A39F9F" }} />;
      default: return <ClockCircleOutlined style={{ color: "#E4A54B" }} />;
    }
  };

  const passengers = tripData.passengers || [];

  const handleEditClick = () => {
    if (onEditTrip) {
      onEditTrip(tripData);
    }
  };

  const handleDeleteClick = () => {
    if (onDeleteTrip) {
      onDeleteTrip(tripData);
    }
  };

  return (
    <>
      <Modal
        open={isOpen}
        onCancel={onClose}
        footer={null}
        width={500}
        zIndex={1050}
        closeIcon={<CloseOutlined style={{ color: "white" }} />}
        styles={{
          header: {
            backgroundColor: headerBgColor,
            padding: "16px 24px",
            borderRadius: "8px 8px 0 0",
            margin: "-20px -24px 12px -24px" 
          },
          title: {
            color: "white",
            margin: 0
          },
          body: {
            maxHeight: '70vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            margin: '0 -24px',
            padding: '12px 24px'
          }
        }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Title level={5} style={{ color: 'white', margin: 0, fontSize: '16px' }}>Trip Details</Title>
            <Space size="middle" style={{ display: 'flex', alignItems: 'center' }}>
              {canEdit && (
                <EditOutlined style={{ color: "white", cursor: "pointer", fontSize: "18px" }} onClick={handleEditClick} />
              )}
              {canDelete && (
                <DeleteOutlined style={{ color: "white", cursor: "pointer", fontSize: "18px" }} onClick={handleDeleteClick} />
              )}
              <CloseOutlined style={{ color: "white", cursor: "pointer", fontSize: "18px", marginLeft: '4px' }} onClick={onClose} />
            </Space>
          </div>
        }
        className="ouber-modal white-close-icon"
        closable={false}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingBottom: '12px' }}>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9f9f9', padding: '12px', borderRadius: '8px' }}>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Deadline of Requests</Text>
              <Text strong>{deadlineDayjs && deadlineDayjs.isValid() ? deadlineDayjs.format('MMMM D, YYYY h:mm A') : "N/A"}</Text>
            </div>
            <div style={{ textAlign: 'right' }}>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Status of Trip</Text>
              <Space>
                {(() => {
                  const status = tripData.status || tripData.trip_status;
                  const isDeadlinePassed = deadlineDayjs ? dayjs().isAfter(deadlineDayjs) : false;
                  const displayStatus = (status === 'Available' && (isDeadlinePassed || isFull)) ? 'Unavailable' : status;
                  
                  return (
                    <>
                      <Text strong>{displayStatus}</Text>
                      {getStatusIcon(displayStatus)}
                    </>
                  );
                })()}
              </Space>
            </div>
          </div>

          <div style={{ border: '1px solid #f0f0f0', padding: '16px', borderRadius: '8px' }}>
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: '4px' }}>Starting Pickup Point</Text>
              <Text strong style={{ fontSize: '16px' }}>{pickupLocation}</Text>
            </div>
            {!(tripData.allowed_multiple_pickup || tripData.isMultiplePickupAllowed) && (
              <div style={{ marginTop: '12px' }}>
                <Alert 
                  message="Reminder: Pickups are only at the designated starting point." 
                  type="warning" 
                  showIcon 
                  style={{ fontSize: '12px' }}
                />
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Trip Route</Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text>{pickupLocation}</Text> 
                <ArrowRightOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} /> 
                <Text>{dropoffLocation}</Text>
              </div>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Date</Text>
              <Text>{tripDate ? dayjs(tripDate).format("MMMM D, YYYY") : "N/A"}</Text>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Pickup Time</Text>
              <Text>{formatTime(tripData.pickup_time) || formatTime(tripData.time)}</Text>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Estimated Arrival</Text>
              <Text>{formatTime(tripData.estimated_arrival_time) || formatTime(tripData.estimatedArrival) || "TBD"}</Text>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Driver</Text>
              <Text>{(tripData.driver && typeof tripData.driver === 'object' && tripData.driver.driver_name) ? tripData.driver.driver_name : (tripData.custom_driver_name || "Unassigned")}</Text>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Contact Number</Text>
              <Text>{(tripData.driver && typeof tripData.driver === 'object' && tripData.driver.contact_number) ? tripData.driver.contact_number : (tripData.custom_driver_contact || "N/A")}</Text>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Vehicle (Plate No.)</Text>
              <Text>
                {(tripData.vehicle && typeof tripData.vehicle === 'object' && tripData.vehicle.vehicle_model) ? tripData.vehicle.vehicle_model : (tripData.custom_vehicle_name || "Unassigned")}
                {" "}({(tripData.vehicle && typeof tripData.vehicle === 'object' && tripData.vehicle.plate_number) ? tripData.vehicle.plate_number : (tripData.custom_vehicle_plate || "N/A")})
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Vehicle Type</Text>
              <Text>{(tripData.vehicle && typeof tripData.vehicle === 'object' && tripData.vehicle.vehicle_type) ? tripData.vehicle.vehicle_type : (tripData.custom_vehicle_type || "N/A")}</Text>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Capacity</Text>
              <Text style={{ color: isFull ? "#cf1322" : "inherit", fontWeight: isFull ? 600 : 400 }}>
                {current}/{total} {isFull && "(Full)"}
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Purpose of Trip</Text>
              <Text>{purpose}</Text>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Organizing Unit</Text>
            <Text>{getUnitAcronym(tripData.organizing_unit)}</Text>
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>Passengers</Text>
              <Text type="secondary" style={{ fontSize: '11px' }}>{passengers.length} Registered</Text>
            </div>
            <div style={{ 
              maxHeight: '200px', 
              overflowY: 'auto', 
              paddingRight: '8px',
              border: '1px solid #f0f0f0',
              borderRadius: '8px',
              padding: '8px'
            }}>
              {passengers.length > 0 ? (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                  gap: '4px 16px' 
                }}>
                  {(() => {
                    const requestsArr = Array.isArray(tripData.requests) 
                      ? tripData.requests 
                      : (typeof tripData.requests === 'object' && tripData.requests !== null 
                          ? Object.values(tripData.requests) 
                          : []);

                    return [...passengers]
                      .sort((a, b) => {
                        const getT = (p) => {
                          const foundReq = requestsArr.find(r => Number(r.employee_id || r.id) === Number(p.employee_id || p.id));
                          const raw = p.estimated_pickup_time || p.pivot?.estimated_pickup_time || foundReq?.estimated_pickup_time || tripData.pickup_time || tripData.time;
                          return dayjs(raw, ["HH:mm:ss", "h:mm a", "H:mm:ss"]);
                        };
                        return getT(a).diff(getT(b));
                      })
                      .map((p) => {
                        const isRequestor = !!(p.is_requestor || p.pivot?.is_requestor);
                        const isPending = p.pivot?.request_status === "Requested to Join" || p.status === "Requested to Join";
                        const rawUnit = getActiveEarliestUnit(p);

                        return (
                          <div key={p.id} style={{ padding: '4px 0', borderBottom: '1px solid #f9f9f9' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minHeight: '28px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', flex: 1, flexWrap: 'wrap', gap: '4px' }}>
                                <Text strong style={{ fontSize: '13px' }}>
                                  {cleanName(p.first_name || p.name)} {cleanName(p.last_name || "")} 
                                  {!p.is_external && (
                                    <Text type="secondary" style={{ fontWeight: 400, fontSize: '11px', color: '#8c8c8c', marginLeft: '4px' }}>
                                      ({getUnitAcronym(rawUnit) !== "N/A" ? getUnitAcronym(rawUnit) : getUnitAcronym(p.first_name || p.name)})
                                    </Text>
                                  )}
                                </Text>
                                {p.is_external && <Tag color="orange" style={{ border: 'none', margin: 0, fontSize: '9px', lineHeight: '16px', marginLeft: '4px' }}>Guest</Tag>}
                                {isRequestor ? (
                                  <Tag color="gold" style={{ border: 'none', margin: 0, fontSize: '10px' }}>Head of the Party</Tag>
                                ) : isPending ? (
                                  <Tag color="warning" style={{ border: 'none', margin: 0, fontSize: '10px' }}>Requested to Join</Tag>
                                ) : (
                                  <Tag color="success" style={{ border: 'none', margin: 0, fontSize: '10px' }}>Approved by AO</Tag>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      });
                  })()}
                </div>

              ) : (
                <div style={{ padding: '8px', textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>No passengers joined yet.</Text>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AdminViewTripModal;
